// Data Pipeline redaction chip vocabulary + live-feed merge helpers. Pure
// functions, unit-tested (flow-masks.test.ts). Ported from the approved
// Claude Design mockup (design-bundle/chips.js §6.2/6.3).
//
// Three chip states: present+valid (solid) / present+invalid (warn) / absent
// (ghost). Dotted border = hashed upstream, shape-verified. Absence is
// visible on purpose — quality gaps are the point.

import { CONVERSION_SIGNALS } from '@/api/types.gen';
import { nyEndOfDayMsExclusive, nyStartOfDayMs } from '@/lib/datetime';
import type {
  FlowEventRedacted,
  FlowLivePage,
} from '@/api/types';
import type {
  FieldCheck,
  HashedPiiPresence,
  HistoryBucket,
  IdentityPresence,
  PiiPresence,
  TicketFlowRedacted,
} from '@/api/types.gen';

// ── FieldCheck → chip state ────────────────────────────────────────────────

export type ChipState = 'ok' | 'bad' | 'off';

export function fcState(fc: FieldCheck): ChipState {
  return fc === 'valid' ? 'ok' : fc === 'invalid' ? 'bad' : 'off';
}

export function fcText(fc: FieldCheck): string {
  return fc === 'valid' ? 'PRESENT, VALID'
    : fc === 'invalid' ? 'PRESENT, INVALID (failed format check)'
    : 'ABSENT';
}

export interface ChipModel {
  letter: string;
  fc: FieldCheck;
  label: string;
  /** Hashed upstream — renders with the dotted-border variant. */
  hashed?: boolean;
}

export function chipTip(chip: ChipModel): string {
  return chip.label.toUpperCase() + ' — ' + (chip.hashed && chip.fc !== 'absent'
    ? 'HASHED UPSTREAM, SHAPE-' + (chip.fc === 'valid' ? 'VERIFIED' : 'INVALID')
    : fcText(chip.fc));
}

/**
 * Address bundle: street/city/state/zip — valid if every present part is
 * valid, invalid if ANY part is invalid, absent when all parts are absent.
 */
export function bundleFc(parts: FieldCheck[]): FieldCheck {
  const present = parts.filter((p) => p !== 'absent');
  if (!present.length) return 'absent';
  return present.some((p) => p === 'invalid') ? 'invalid' : 'valid';
}

// ── PII group: E P F L A (raw, or dotted hashed variant) ───────────────────

/**
 * Rows carrying only pre-hashed PII (server-pixel enhanced conversions)
 * render the same letters with the dotted variant.
 */
export function piiChips(pii: PiiPresence, hashed: HashedPiiPresence): { hashed: boolean; chips: ChipModel[] } {
  const rawPresent = Object.values(pii).some((v) => v !== 'absent');
  const hashPresent = Object.values(hashed).some((v) => v !== 'absent');
  if (!rawPresent && hashPresent) {
    return {
      hashed: true,
      chips: [
        { letter: 'E', fc: hashed.email_sha256, label: 'pii email (sha256)', hashed: true },
        { letter: 'P', fc: hashed.phone_sha256, label: 'pii phone (sha256)', hashed: true },
        { letter: 'F', fc: hashed.first_name_sha256, label: 'pii first name (sha256)', hashed: true },
        { letter: 'L', fc: hashed.last_name_sha256, label: 'pii last name (sha256)', hashed: true },
        {
          letter: 'A',
          fc: bundleFc([hashed.city_sha256, hashed.state_sha256, hashed.zip5_sha256, hashed.country_sha256]),
          label: 'pii address bundle (sha256)',
          hashed: true,
        },
      ],
    };
  }
  return {
    hashed: false,
    chips: [
      { letter: 'E', fc: pii.email, label: 'pii email' },
      { letter: 'P', fc: pii.phone, label: 'pii phone' },
      { letter: 'F', fc: pii.first_name, label: 'pii first name' },
      { letter: 'L', fc: pii.last_name, label: 'pii last name' },
      { letter: 'A', fc: bundleFc([pii.street1, pii.city, pii.state, pii.zip5]), label: 'pii address bundle' },
    ],
  };
}

// ── Price group: V C O — business data is shown openly elsewhere; the chips
// only signal presence. `null` for non-purchase events (renders a dash). ────

export function priceChips(
  ev: Pick<FlowEventRedacted, 'event_type' | 'value' | 'currency' | 'ticket_id'>,
): ChipModel[] | null {
  if (!isEventType(ev.event_type, 'purchase')) return null;
  return [
    { letter: 'V', fc: ev.value != null ? 'valid' : 'absent', label: 'price value' },
    { letter: 'C', fc: ev.currency != null ? 'valid' : 'absent', label: 'price currency' },
    { letter: 'O', fc: ev.ticket_id != null ? 'valid' : 'absent', label: 'order / ticket id' },
  ];
}

// ── Identity envelope: 10 fixed slots, established mask order ──────────────

export const IDENTITY_SLOTS: ReadonlyArray<readonly [string, keyof IdentityPresence]> = [
  ['e', 'beacon_event_id'], ['f', 'fbp'], ['c', 'fbc'], ['F', 'fbclid'], ['g', 'gclid'],
  ['G', 'ga_client_id'], ['s', 'ga_session_id'], ['t', 'ttclid'], ['T', 'ttp'], ['i', 'ttd_impression_id'],
];

export function identityChips(identity: IdentityPresence): ChipModel[] {
  return IDENTITY_SLOTS.map(([letter, key]) => ({
    letter,
    fc: identity[key],
    label: 'identity ' + key,
  }));
}

// ── Event type comparison ──────────────────────────────────────────────────

/**
 * Does a web event's type name `want`? Compared without case, everywhere.
 *
 * The two web lanes spell the same type differently: the live tap carries the
 * wire's lowercase `purchase` / `pageview`, while the stored rows that the
 * history lane groups by carry `Purchase` / `Pageview`. An exact `=== 'purchase'`
 * against a history row matched nothing, so every purchase-based history rate
 * divided zero by zero and read as 0%.
 */
export function isEventType(eventType: string | null | undefined, want: string): boolean {
  return (eventType ?? '').toLowerCase() === want.toLowerCase();
}

/** A purchase event, in either lane's spelling. */
export function isPurchase(r: { event_type: string }): boolean {
  return isEventType(r.event_type, 'purchase');
}

// ── Rates ──────────────────────────────────────────────────────────────────

/**
 * A percentage, or `null` when there is nothing to divide by. A rate with no
 * observations is unknown, not 0%: every caller renders `null` as "—" and
 * suppresses any delta computed from it.
 */
export function ratePct(numerator: number, denominator: number): number | null {
  return denominator > 0 ? (numerator / denominator) * 100 : null;
}

// ── Ticket chips (encore context panel §6.5) ───────────────────────────────

export function ticketChips(t: TicketFlowRedacted): { contact: ChipModel[]; money: ChipModel[] } {
  return {
    contact: [
      { letter: 'E', fc: t.emails, label: 'ticket emails' },
      { letter: 'P', fc: t.phones, label: 'ticket phones' },
      { letter: 'N', fc: t.name, label: 'ticket buyer name' },
      { letter: 'A', fc: t.address, label: 'ticket address' },
    ],
    money: [
      { letter: 'V', fc: t.amount, label: 'ticket amount' },
      { letter: 'C', fc: t.currency, label: 'ticket currency' },
    ],
  };
}

// ── Conversion signal mask (13-char positional, CONVERSION_SIGNALS order) ──

export interface MaskChip {
  letter: string;
  label: string;
  on: boolean;
}

/** Decode the positional presence mask (e.g. "VCOEPNF--A-IW") into labeled chips. */
export function decodeSignalMask(mask: string): MaskChip[] {
  return CONVERSION_SIGNALS.map(([letter, label], i) => ({
    letter,
    label,
    on: mask.charAt(i) === letter,
  }));
}

// ── Live feed merge / freeze helpers ───────────────────────────────────────
//
// Full-page protocol: every poll fetches the server's full newest-200 window
// (no incremental cursor — an `after_seq` cursor wedges forever after a tap
// restart resets the seq space, and never re-serves rows the server mutated
// in place, e.g. mark_resolved flipping person_id_resolved on already-served
// events).

/** Rendered-row cap — matches the server's newest-200 page window. */
export const FEED_CAP = 200;

/**
 * Merge a full page into the accumulated feed: REPLACE-by-seq (the incoming
 * copy wins — it carries fresher server-side mutations like the pending→
 * resolved person flip), new seqs prepend, newest first, capped.
 */
export function mergeFeed(
  existing: readonly FlowEventRedacted[],
  incoming: readonly FlowEventRedacted[],
  cap: number = FEED_CAP,
): FlowEventRedacted[] {
  const bySeq = new Map<number, FlowEventRedacted>();
  for (const e of existing) bySeq.set(Number(e.seq), e);
  for (const e of incoming) bySeq.set(Number(e.seq), e);
  return [...bySeq.values()].sort((a, b) => Number(b.seq) - Number(a.seq)).slice(0, cap);
}

/**
 * Apply one polled page:
 * - paused ⇒ the frozen list is returned UNCHANGED, same reference (§6.1
 *   freeze — pages that land while frozen are dropped);
 * - `started_at` changed vs the previous page ⇒ the tap buffer restarted and
 *   its seq space reset — the whole list is REPLACED by the page;
 * - otherwise merge with replace-by-seq semantics.
 * A no-op page (nothing new, nothing mutated) returns the same reference so
 * steady-state polls cause no render churn (TanStack structural sharing
 * preserves unchanged event identities across fetches).
 */
export function feedAfterPage(
  prev: FlowEventRedacted[],
  page: FlowLivePage | undefined,
  paused: boolean,
  prevStartedAt: string | null,
  cap: number = FEED_CAP,
): FlowEventRedacted[] {
  if (paused || !page) return prev;
  const restarted = prevStartedAt != null && page.started_at !== prevStartedAt;
  if (!restarted && page.events.length === 0) return prev;
  const next = mergeFeed(restarted ? [] : prev, page.events, cap);
  return next.length === prev.length && next.every((e, i) => e === prev[i]) ? prev : next;
}

// ── History bucket axis (§7) ───────────────────────────────────────────────

const HOUR_MS = 3_600_000;

/**
 * Enumerate the FULL bucket axis for a requested range. The server only
 * returns buckets that have rows — an axis built from returned keys would
 * compress time (empty buckets vanish) and inflate range averages.
 *
 * The range is HALF-OPEN, `[from, to)`, the same as the server's `< to`: the
 * screen sends the next New York midnight as `to`, so a one-day range is one
 * bucket. (It used to send that midnight minus 1 ms against an inclusive walk,
 * which only came out right by accident of the millisecond.)
 *
 * The axis is NEW YORK, because the wire is: `flow_history` anchors every day
 * bucket on an NY midnight (`…T04:00:00Z` EDT / `…T05:00:00Z` EST, see
 * `ny_bucket_start`), so a `(ms / DAY_MS) * DAY_MS` UTC axis collided with no
 * row at all and every day-grain chart drew flat zero while the KPI tiles —
 * summed straight from the rows — showed the real numbers. Day steps walk the
 * NY calendar (23 h and 25 h across a DST transition, never `+= DAY_MS`); hour
 * steps are UTC hours, which New York's whole-hour offsets already agree with.
 *
 * Emitted as canonical ISO instants — [`bucketize`] keys both sides on
 * `Date.parse`, so the server's RFC3339 flavor never has to match
 * byte-for-byte.
 */
export function enumerateBuckets(fromIso: string, toIso: string, bucket: HistoryBucket): string[] {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [];
  const out: string[] = [];
  if (bucket === 'hour') {
    for (let t = Math.floor(from / HOUR_MS) * HOUR_MS; t < to; t += HOUR_MS) {
      out.push(new Date(t).toISOString());
    }
    return out;
  }
  for (let t = nyStartOfDayMs(from); t < to; t = nyEndOfDayMsExclusive(t)) {
    out.push(new Date(t).toISOString());
  }
  return out;
}

/** The exclusive end of one bucket: the next hour, or the next NY midnight. */
export function bucketEndMs(startIso: string, bucket: HistoryBucket): number {
  const start = Date.parse(startIso);
  return bucket === 'hour' ? start + HOUR_MS : nyEndOfDayMsExclusive(start);
}

/**
 * Where each bucket sits against the conversions history floor (the GC
 * watermark: every conversion before it has been deleted).
 *
 * - `before`: the bucket ends at or before the floor — nothing in it survives.
 * - `partial`: the floor falls inside the bucket — some of it was deleted, so
 *   its counts are short and no comparison may be drawn from it.
 * - `full`: the bucket starts at or after the floor.
 */
export type FloorState = 'before' | 'partial' | 'full';

export function bucketFloorStates(
  buckets: readonly string[],
  floorMs: number | null,
  bucket: HistoryBucket,
): FloorState[] {
  return buckets.map((start) => {
    if (floorMs == null) return 'full';
    if (bucketEndMs(start, bucket) <= floorMs) return 'before';
    return Date.parse(start) < floorMs ? 'partial' : 'full';
  });
}

/**
 * Sum `val` over the rows matching `pred`, one number per enumerated bucket.
 *
 * A row keys on the epoch-ms of its bucket instant, not on its raw string, so
 * the server's RFC3339 flavor never has to match byte-for-byte. Nothing is
 * snapped: EVERY day lane anchors a bucket on the same NY midnight
 * [`enumerateBuckets`] walks — `web_events` and `tickets` through
 * `flow_history::ny_bucket_start`, and conversions through
 * `shared::time::start_of_ny_day_ms` since `flow_history_cells` stopped
 * flooring `event_at` on a UTC day — while an hour bucket agrees outright
 * because New York's offsets are whole hours.
 *
 * That makes the instant the slot, and it is deliberately unforgiving: a lane
 * that ever drifts off the NY anchor draws zero rather than being quietly
 * placed on a neighbouring slot. A silent placement is what let a UTC-keyed
 * conversions lane look answered for as long as it did.
 *
 * Buckets with no rows yield 0 — empty periods render as zero bars, never
 * vanish. (A RATE over an empty bucket is a gap, not 0%: see `ratePct`.) `_grain` is kept so every call site still states the grain its axis
 * was built at, and so a lane needing grain-specific keying has somewhere to
 * put it; the keying itself no longer branches on it.
 */
export function bucketize<T extends { bucket: string }>(
  rows: readonly T[],
  buckets: readonly string[],
  _grain: HistoryBucket,
  pred: (r: T) => boolean,
  val: (r: T) => number,
): number[] {
  const m = new Map<number, number>();
  for (const r of rows) {
    if (!pred(r)) continue;
    const t = Date.parse(r.bucket);
    m.set(t, (m.get(t) ?? 0) + val(r));
  }
  return buckets.map((b) => m.get(Date.parse(b)) ?? 0);
}

// ── Admin reveal countdown ─────────────────────────────────────────────────
// Anchored to the wall-clock `revealedAt` stamped at mutation success — the
// TTL is ABSOLUTE (§6.4). Deriving from the anchor (instead of a mounted
// countdown) means remounts can't restart the window: re-display within the
// window shows the true remaining time, expiry fires even while hidden.

export const REVEAL_TTL_SECS = 60;

/** Milliseconds until an active reveal expires (0 ⇒ already elapsed — clear it). */
export function revealMsLeft(revealedAtMs: number, nowMs: number, ttlSecs: number = REVEAL_TTL_SECS): number {
  return Math.max(0, revealedAtMs + ttlSecs * 1000 - nowMs);
}

/** Whole seconds left before a revealed payload auto-hides; clamped ≥ 0. */
export function revealSecondsLeft(revealedAtMs: number, nowMs: number, ttlSecs: number = REVEAL_TTL_SECS): number {
  return Math.max(0, ttlSecs - Math.floor((nowMs - revealedAtMs) / 1000));
}
